import React, { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { useToastContext, ToastVariant } from '../../context';
import { useUserPartnerInformation } from '../../hooks';
import { fetchServiceTemplates, updateServiceTemplates } from '../../services/partner/partner.api';
import { Button } from '../../components/UI/Button';
import { Card } from '../../components/UI/Card';
import { PageLayout } from '../../layout/PageLayout';

const ServiceTemplates = () => {
    const [user] = useAuthState(auth);
    const { addToast } = useToastContext();
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState<any>({});
    const [saving, setSaving] = useState(false);

    // Define standard service states we want to support templates for
    const SERVICE_STATES = [
        'SERVICE_REQUEST_RECEIVED',
        'PRODUCT_RECEIVED',
        'IN_ASSESSMENT',
        'CLIENT_APPROVAL_NEEDED',
        'IN_REPAIR',
        'COMPLETED',
        'SHIPPED_BACK'
    ];

    const { userPartnerInformation } = useUserPartnerInformation(user?.uid);
    const partnerId = userPartnerInformation?.data?.partnerId;

    useEffect(() => {
        if (partnerId) {
            loadTemplates();
        }
    }, [partnerId]);

    const loadTemplates = async () => {
        if (!partnerId) return;
        setLoading(true);
        try {
            const res = await fetchServiceTemplates(partnerId);
            if (res && (res as any).data) {
                setTemplates((res as any).data || {});
            }
        } catch (error) {
            console.error('Failed to load templates', error);
            // Fail silently or toast
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!partnerId) return;
        setSaving(true);
        try {
            await updateServiceTemplates(partnerId, templates);
            // Toast success
            addToast(ToastVariant.SUCCESS, { title: "Templates saved successfully" });
        } catch (error) {
            console.error('Failed to save templates', error);
            addToast(ToastVariant.ERROR, { title: "Failed to save templates" });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (state: string, field: 'subject' | 'body', value: string) => {
        setTemplates((prev: any) => ({
            ...prev,
            [state]: {
                ...prev[state],
                [field]: value
            }
        }));
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="flex justify-center items-center h-64">
                    Loading...
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <div className="flex flex-col gap-6">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-bold">Service Templates</h1>
                </div>

                <Card>
                    <div className="mb-6">
                        <p className="text-gray-500 text-sm">
                            Customize the emails sent to customers when their service case status changes.
                            Use <code className="bg-gray-100 px-1 rounded">{'{customerName}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{productName}'}</code>, and <code className="bg-gray-100 px-1 rounded">{'{ticketId}'}</code> as placeholders.
                        </p>
                    </div>

                    <div className="space-y-8">
                        {SERVICE_STATES.map(state => (
                            <div key={state} className="border-b border-gray-100 pb-6 last:border-0 last:pb-0">
                                <h3 className="text-lg font-semibold mb-3 capitalize text-gray-800">
                                    {state.replace(/_/g, ' ').toLowerCase()}
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line</label>
                                        <input
                                            type="text"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
                                            value={templates[state]?.subject || ''}
                                            onChange={(e) => handleChange(state, 'subject', e.target.value)}
                                            placeholder={`Subject for ${state.toLowerCase()}...`}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Body</label>
                                        <textarea
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition min-h-[120px]"
                                            value={templates[state]?.body || ''}
                                            onChange={(e) => handleChange(state, 'body', e.target.value)}
                                            placeholder={`Email content for ${state.toLowerCase()}...`}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end mt-8 pt-4 border-t border-gray-100">
                        <Button onClick={handleSave} isLoading={saving}>
                            Save Changes
                        </Button>
                    </div>
                </Card>
            </div>
        </PageLayout>
    );
};

export default ServiceTemplates;
